import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Pencil, Save, X, Eye, ChevronLeft, ChevronRight, List, Table, Search, GripVertical, Download, SlidersHorizontal } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { adminManagementApi } from '../../services/api';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import ColumnSettingsModal from '../../components/common/ColumnSettingsModal';

const ROLE_NAMES = ['Guest', 'User', 'PowerUser', 'AppAdmin'];

const RolesCapabilitiesPage = () => {
  const [matrix, setMatrix] = useState(null);
  const [, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [listCollapsed, setListCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { notify } = useNotifications();

  // List view state
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dirtyRoles, setDirtyRoles] = useState({});

  // Datasheet state
  const [sheetEditing, setSheetEditing] = useState(false);
  const [dirtySheet, setDirtySheet] = useState({});
  const [gridFilters, setGridFilters] = useState({});
  const [columnOrder, setColumnOrder] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [matrixRes, permRes] = await Promise.all([
        adminManagementApi.getRoleMatrix(),
        adminManagementApi.getPermissions()
      ]);
      setMatrix(matrixRes.data);
      setPermissions(permRes.data);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Re-select after reload
  useEffect(() => {
    if (selected && matrix?.permissions) {
      const refreshed = matrix.permissions.find(p => p.id === selected.id);
      if (refreshed) setSelected(refreshed);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrix]);

  const startEdit = () => {
    if (!selected) return;
    setEditing({ permId: selected.id });
    setDirtyRoles({});
  };

  const cancelEdit = (force = false) => {
    if (!force && (Object.keys(dirtyRoles).length > 0 || Object.keys(dirtySheet).length > 0)) {
      notify.confirm('Discard unsaved changes?', () => { setEditing(null); setDirtyRoles({}); setSheetEditing(false); setDirtySheet({}); });
      return;
    }
    setEditing(null);
    setDirtyRoles({});
    setSheetEditing(false);
    setDirtySheet({});
  };

  const getRoleAccess = (roleName) => {
    if (dirtyRoles[roleName] !== undefined) return dirtyRoles[roleName];
    return selected?.roles[roleName] || 'None';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (viewMode === 'datasheet') {
        const updates = Object.entries(dirtySheet).map(([key, accessType]) => {
          const [role, permId] = key.split('-');
          return { Role: role, PermissionDefId: parseInt(permId), AccessType: accessType };
        });
        if (updates.length > 0) await adminManagementApi.updateRoleMatrix(updates);
        setDirtySheet({});
        setSheetEditing(false);
      } else if (editing && Object.keys(dirtyRoles).length > 0) {
        const updates = Object.entries(dirtyRoles).map(([role, access]) => ({
          Role: role, PermissionDefId: selected.id, AccessType: access
        }));
        await adminManagementApi.updateRoleMatrix(updates);
        setEditing(null);
        setDirtyRoles({});
      }
      await load();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSheetClick = (permId, roleName, currentAccess) => {
    if (!sheetEditing) return;
    const key = `${roleName}-${permId}`;
    const next = currentAccess === 'Grant' ? 'None' : 'Grant';
    const original = matrix.permissions.find(p => p.id === permId)?.roles[roleName] || 'None';
    if (next === original) {
      const n = { ...dirtySheet };
      delete n[key];
      setDirtySheet(n);
    } else {
      setDirtySheet({ ...dirtySheet, [key]: next });
    }
  };

  const getSheetAccess = (permId, roleName) => {
    const key = `${roleName}-${permId}`;
    if (dirtySheet[key] !== undefined) return dirtySheet[key];
    return matrix?.permissions?.find(p => p.id === permId)?.roles[roleName] || 'None';
  };

  const accessStyle = (access) => ({
    fontWeight: 600,
    color: access === 'Grant' ? 'var(--color-success)' : 'var(--color-error)'
  });

  const isEditing = editing !== null || sheetEditing;
  const hasDirty = Object.keys(dirtyRoles).length > 0 || Object.keys(dirtySheet).length > 0;

  const filteredPerms = (matrix?.permissions || []).filter(p =>
    !searchTerm ||
    (p.displayName || p.permissionKey).toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.permissionKey?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Transform matrix data into flat rows for FilterableDataGrid
  const gridData = filteredPerms.map(p => {
    const row = { id: p.id, category: p.category, displayName: p.displayName || p.permissionKey, permissionKey: p.permissionKey };
    ROLE_NAMES.forEach(r => { row[r] = getSheetAccess(p.id, r) === 'Grant' ? 'Grant' : 'Deny'; });
    return row;
  });

  const allGridData = (matrix?.permissions || []).map(p => {
    const row = { id: p.id, category: p.category, displayName: p.displayName || p.permissionKey, permissionKey: p.permissionKey };
    ROLE_NAMES.forEach(r => { row[r] = (p.roles[r] || 'None') === 'Grant' ? 'Grant' : 'Deny'; });
    return row;
  });

  const gridColumns = [
    { key: 'category', label: 'Category', width: 120 },
    { key: 'displayName', label: 'Display Name', width: 200 },
    { key: 'permissionKey', label: 'Permission Key', width: 260 },
    ...ROLE_NAMES.map(r => ({ key: r, label: r, width: 110 }))
  ];

  const renderEditableCell = (row, column, value, isEdited) => {
    if (!ROLE_NAMES.includes(column.key)) return <span style={{ color: 'var(--color-gray-400)', fontStyle: 'italic' }}>{value}</span>;
    const permId = row.id;
    const roleName = column.key;
    const access = getSheetAccess(permId, roleName);
    const key = `${roleName}-${permId}`;
    const isDirty = dirtySheet[key] !== undefined;
    return (
      <span
        onClick={sheetEditing ? (e) => { e.stopPropagation(); handleSheetClick(permId, roleName, access); } : undefined}
        style={{
          cursor: sheetEditing ? 'pointer' : 'default',
          fontWeight: 600,
          color: access === 'Grant' ? 'var(--color-success)' : 'var(--color-error)',
          backgroundColor: isDirty ? '#fffde7' : undefined,
          display: 'block',
          textAlign: 'center'
        }}
      >
        {access === 'Grant' ? 'Grant' : 'Deny'}
      </span>
    );
  };

  if (loading) return <div className="dev-page loading">Loading...</div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <button className={`tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`} onClick={() => { setViewMode('list'); cancelEdit(); }} title="List view"><List size={18} /></button>
          <button className={`tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`} onClick={() => { setViewMode('datasheet'); setEditing(null); setSelected(null); setDirtyRoles({}); }} title="Datasheet view"><Table size={18} /></button>
          <span className="stui-module-header-meta">
            {filteredPerms.length !== (matrix?.permissions?.length || 0)
              ? `${filteredPerms.length} of ${matrix?.permissions?.length || 0} permissions`
              : `${matrix?.permissions?.length || 0} permissions`
            }
          </span>
        </div>
        <div className="stui-module-header-actions">
          {/* CRUD: no + or trash on this page — permissions are managed on Permissions List */}
          {viewMode === 'list' ? (
            <button className={`stui-crud-btn ${isEditing ? '' : !selected ? 'disabled' : ''}`} onClick={isEditing ? cancelEdit : startEdit} disabled={!selected && !isEditing} title={isEditing ? 'Cancel' : 'Edit'}>
              {isEditing ? <X size={18} /> : <Pencil size={18} />}
            </button>
          ) : (
            <button className={`stui-crud-btn`} onClick={sheetEditing ? cancelEdit : () => setSheetEditing(true)} title={sheetEditing ? 'Cancel' : 'Edit'}>
              {sheetEditing ? <X size={18} /> : <Pencil size={18} />}
            </button>
          )}
          <button className={`stui-crud-btn ${!hasDirty ? 'disabled' : ''}`} onClick={handleSave} disabled={!hasDirty || saving} title="Save"><Save size={18} /></button>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={14} style={{ position: 'absolute', left: '8px', color: 'var(--color-gray-400)', pointerEvents: 'none' }} />
            <input className="stui-field-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search..." style={{ paddingLeft: '28px', width: '160px', height: '32px' }} />
          </div>
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); }}><RefreshCw size={16} /><span>Refresh</span></button>
                {viewMode === 'datasheet' && (
                  <>
                    <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); /* TODO: export */ }}><Download size={16} /><span>Download</span></button>
                    <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); setShowColumnSettings(true); }}><SlidersHorizontal size={16} /><span>Modify View</span></button>
                    {/* No bulk delete — permissions managed on Permissions List page */}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="stui-list-view">
          {listCollapsed ? (
            <div className="stui-list-panel stui-collapsed">
              <button onClick={() => setListCollapsed(false)} className="stui-list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button>
            </div>
          ) : (
            <div className="stui-list-panel">
              <div className="stui-list-panel-header">
                <button onClick={() => setListCollapsed(true)} className="stui-list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
              </div>
              <div className="stui-list-scroll">
                {filteredPerms.length === 0 ? (
                  <div className="task-list-empty">No permissions found</div>
                ) : filteredPerms.map(p => {
                  const grantCount = ROLE_NAMES.filter(r => (p.roles[r] || 'None') === 'Grant').length;
                  return (
                    <button key={p.id} onClick={() => { setSelected(p); setEditing(null); setDirtyRoles({}); }} className={`stui-list-item ${selected?.id === p.id ? 'selected' : ''}`}>
                      
                        <span className="stui-list-item-primary">{p.displayName || p.permissionKey}</span>
                        <span className="stui-list-item-secondary">{p.category} • {grantCount}/{ROLE_NAMES.length} roles</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="stui-detail-panel">
            {selected ? (
              <div className="stui-detail-content">
                <div className="stui-detail-grid">
                  <div className="stui-col-24"><div className="stui-detail-section-header">Permission Details</div></div>
                  <div className="stui-field-group stui-col-8"><label>Category</label><input className="stui-field-input" value={selected.category || ''} readOnly /></div>
                  <div className="stui-field-group stui-col-8"><label>Display Name</label><input className="stui-field-input" value={selected.displayName || ''} readOnly /></div>
                  <div className="stui-field-group stui-col-8"><label>Permission Key</label><input className="stui-field-input derived-field" value={selected.permissionKey} readOnly /></div>
                  <div className="stui-field-group stui-col-24"><label>Description</label><input className="stui-field-input" value={selected.description || ''} readOnly /></div>
                </div>
                <div className="stui-detail-grid">
                  <div className="stui-col-24"><div className="stui-detail-section-header">Role Defaults</div></div>
                  {ROLE_NAMES.map(r => {
                    const access = getRoleAccess(r);
                    const isDirty = dirtyRoles[r] !== undefined;
                    return (
                      <div key={r} className="stui-field-group stui-col-6">
                        <label>{r}</label>
                        {isEditing ? (
                          <select
                            className={`stui-field-input${isDirty ? ' dirty' : ''}`}
                            value={access === 'Grant' ? 'Grant' : 'Deny'}
                            onChange={e => {
                              const val = e.target.value;
                              const original = (selected.roles[r] || 'None') === 'Grant' ? 'Grant' : 'Deny';
                              const newDirty = { ...dirtyRoles };
                              if (val === original) { delete newDirty[r]; }
                              else { newDirty[r] = val === 'Grant' ? 'Grant' : 'None'; }
                              setDirtyRoles(newDirty);
                            }}
                          >
                            <option value="Grant" style={{ color: 'var(--color-success)', fontWeight: 600 }}>Grant</option>
                            <option value="Deny" style={{ color: 'var(--color-error)', fontWeight: 600 }}>Deny</option>
                          </select>
                        ) : (
                          <input className="stui-field-input" value={access === 'Grant' ? 'Grant' : 'Deny'} readOnly style={accessStyle(access === 'Grant' ? 'Grant' : 'Deny')} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="dev-detail-empty">
                <Eye size={48} />
                <p>Select a permission to view role defaults</p>
                <p className="hint">Or switch to datasheet view for the full matrix</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="stui-datasheet-view stui-no-column-resize stui-roles-capabilities-datasheet" style={{ backgroundColor: 'var(--color-bg-white)' }}>
          <FilterableDataGrid
            data={gridData}
            allData={allGridData}
            columns={gridColumns}
            filters={gridFilters}
            setFilters={setGridFilters}
            columnOrder={columnOrder}
            hiddenColumns={hiddenColumns}
            columnWidths={{}}
            editModeEnabled={true}
            editedData={{}}
            renderEditableCell={renderEditableCell}
          />
        </div>
      )}
      <ColumnSettingsModal
        isOpen={showColumnSettings}
        onClose={() => setShowColumnSettings(false)}
        columns={gridColumns}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        onApply={({ columnOrder: newOrder, hiddenColumns: newHidden }) => {
          setColumnOrder(newOrder);
          setHiddenColumns(newHidden);
          setShowColumnSettings(false);
        }}
      />
    </div>
  );
};

export default RolesCapabilitiesPage;
